/**
 * Frontend code generation. Turns canonical `PatternRecord`s into each
 * language's text on the fly, with selectable casing and (C++ only) declaration
 * style. Pure functions — no React, no I/O.
 */

import type { PatternRecord } from "../data/store";

/* ------------------------------- casing ---------------------------------- */

export type Casing =
    | "screaming"
    | "snake"
    | "pascal"
    | "camel"
    | "kebab"
    | "upper"
    | "lower";

export const CASINGS = [
    { key: "screaming", label: "SCREAMING_SNAKE" },
    { key: "snake", label: "snake_case" },
    { key: "pascal", label: "PascalCase" },
    { key: "camel", label: "camelCase" },
    { key: "kebab", label: "kebab-case" },
    { key: "upper", label: "UPPERCASE" },
    { key: "lower", label: "lowercase" },
] as const satisfies ReadonlyArray<{ key: Casing; label: string }>;

export const CASING_KEYS = CASINGS.map((c) => c.key);

/** Split a name into words across PascalCase/camelCase boundaries + separators. */
function words(name: string): string[] {
    return name
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
        .replace(/[_\-\s]+/g, " ")
        .trim()
        .split(" ")
        .filter(Boolean);
}

const cap = (w: string) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();

export function applyCasing(name: string, casing: Casing): string {
    const w = words(name);
    switch (casing) {
        case "screaming":
            return w.map((x) => x.toUpperCase()).join("_");
        case "snake":
            return w.map((x) => x.toLowerCase()).join("_");
        case "pascal":
            return w.map(cap).join("");
        case "camel":
            return w.map((x, i) => (i === 0 ? x.toLowerCase() : cap(x))).join("");
        case "kebab":
            return w.map((x) => x.toLowerCase()).join("-");
        case "upper":
            return w.join("").toUpperCase();
        case "lower":
            return w.join("").toLowerCase();
    }
}

/* ----------------------------- cpp style --------------------------------- */

export type CppStyle = "define" | "constexpr" | "inline-constexpr";

export const CPP_STYLES = [
    { key: "define", label: "#define" },
    { key: "constexpr", label: "constexpr" },
    { key: "inline-constexpr", label: "inline constexpr" },
] as const satisfies ReadonlyArray<{ key: CppStyle; label: string }>;

export const CPP_STYLE_KEYS = CPP_STYLES.map((s) => s.key);

/* ----------------------------- languages --------------------------------- */

export type Language = {
    key: string;
    label: string;
    /** tokenizer + LangLogo hint */
    language: string;
    filename: string;
    defaultCasing: Casing;
    hasCppStyle?: boolean;
};

export const LANGUAGES: Language[] = [
    { key: "cpp", label: "C++", language: "cpp", filename: "patterns.hpp", defaultCasing: "screaming", hasCppStyle: true },
    { key: "csharp", label: "C#", language: "csharp", filename: "Patterns.cs", defaultCasing: "pascal" },
    { key: "json", label: "JSON", language: "json", filename: "patterns.json", defaultCasing: "lower" },
    { key: "rust", label: "Rust", language: "rust", filename: "patterns.rs", defaultCasing: "screaming" },
    { key: "python", label: "Python", language: "python", filename: "patterns.py", defaultCasing: "screaming" },
];

export const getLanguage = (key: string): Language | undefined =>
    LANGUAGES.find((l) => l.key === key);

/* ----------------------------- generation -------------------------------- */

export type GenOptions = { casing: Casing; cppStyle: CppStyle };

function groupByDll(records: PatternRecord[]): Array<[string, PatternRecord[]]> {
    const map = new Map<string, PatternRecord[]>();
    for (const r of records) {
        const arr = map.get(r.dll);
        if (arr) arr.push(r);
        else map.set(r.dll, [r]);
    }
    return [...map];
}

function header(comment: string, n: number, updated: string): string {
    const when = updated ? ` · updated ${updated}` : "";
    return `${comment} cspatterns.dev — ${n} patterns${when}\n${comment} auto-generated — do not edit by hand`;
}

function genCpp(records: PatternRecord[], { casing, cppStyle }: GenOptions, updated: string): string {
    const out = [header("//", records.length, updated), "", "#pragma once"];
    if (cppStyle !== "define") out.push("#include <string_view>");
    out.push("");
    for (const [dll, recs] of groupByDll(records)) {
        out.push(`// ${dll}`);
        for (const r of recs) {
            const n = applyCasing(r.name, casing);
            if (cppStyle === "define") out.push(`#define ${n} "${r.pattern}"`);
            else if (cppStyle === "constexpr") out.push(`constexpr const char* ${n} = "${r.pattern}";`);
            else out.push(`inline constexpr std::string_view ${n} = "${r.pattern}";`);
        }
        out.push("");
    }
    return out.join("\n").trimEnd() + "\n";
}

function genCsharp(records: PatternRecord[], { casing }: GenOptions, updated: string): string {
    const out = [header("//", records.length, updated), "", "public static class Patterns", "{"];
    for (const [dll, recs] of groupByDll(records)) {
        out.push(`    // ${dll}`);
        for (const r of recs) out.push(`    public const string ${applyCasing(r.name, casing)} = "${r.pattern}";`);
        out.push("");
    }
    if (out.at(-1) === "") out.pop();
    out.push("}");
    return out.join("\n") + "\n";
}

function genRust(records: PatternRecord[], { casing }: GenOptions, updated: string): string {
    const out = [header("//", records.length, updated), ""];
    for (const [dll, recs] of groupByDll(records)) {
        out.push(`// ${dll}`);
        for (const r of recs) out.push(`pub const ${applyCasing(r.name, casing)}: &str = "${r.pattern}";`);
        out.push("");
    }
    return out.join("\n").trimEnd() + "\n";
}

function genPython(records: PatternRecord[], { casing }: GenOptions, updated: string): string {
    const out = [header("#", records.length, updated), ""];
    for (const [dll, recs] of groupByDll(records)) {
        out.push(`# ${dll}`);
        for (const r of recs) out.push(`${applyCasing(r.name, casing)} = "${r.pattern}"`);
        out.push("");
    }
    return out.join("\n").trimEnd() + "\n";
}

function genJson(records: PatternRecord[], { casing }: GenOptions): string {
    const obj: Record<string, Record<string, string>> = {};
    for (const r of records) {
        (obj[r.dll] ??= {})[applyCasing(r.name, casing)] = r.pattern;
    }
    return JSON.stringify(obj, null, 2) + "\n";
}

function genDeclarations(records: PatternRecord[], updated: string): string {
    const withDecl = records.filter((r) => r.declaration);
    const out = [header("//", withDecl.length, updated), ""];
    for (const [dll, recs] of groupByDll(withDecl)) {
        out.push(`// ${dll}`);
        for (const r of recs) out.push(r.declaration);
        out.push("");
    }
    return out.join("\n").trimEnd() + "\n";
}

/** Generate the text for a pattern language (or `declarations`). */
export function generate(
    langKey: string,
    records: PatternRecord[],
    opts: GenOptions,
    updated: string,
): string {
    switch (langKey) {
        case "cpp":
            return genCpp(records, opts, updated);
        case "csharp":
            return genCsharp(records, opts, updated);
        case "json":
            return genJson(records, opts);
        case "rust":
            return genRust(records, opts, updated);
        case "python":
            return genPython(records, opts, updated);
        case "declarations":
            return genDeclarations(records, updated);
        default:
            return "";
    }
}
